/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – AlertPanel.tsx FINAL
// Adăugat: Export CSV alerte + Export PDF + filtrare completă
// ============================================================

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Input, Select, Button, Modal, Timeline, message, Popconfirm } from 'antd';
import {
  AlertOutlined, CheckCircleOutlined, ClockCircleOutlined,
  FireOutlined, WarningOutlined, ExclamationCircleOutlined,
  BellOutlined, SearchOutlined, CheckOutlined,
  EnvironmentOutlined, CloseCircleOutlined, DownloadOutlined,
  FilePdfOutlined, FileExcelOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { jsPDF } from 'jspdf';

import { Alert, AlertSeverity, AlertStatus } from '../../types';
import { useSimulatorData } from '../../context/SimulatorContext';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;

// ─── Constante ────────────────────────────────────────────

const SEV_COLOR: Record<AlertSeverity, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#84cc16',
};
const SEV_BG: Record<AlertSeverity, string> = {
  critical: '#fff5f5', high: '#fff7ed', medium: '#fffbf0', low: '#f7fee7',
};
const SEV_LABEL: Record<AlertSeverity, string> = {
  critical: 'Critic', high: 'Ridicat', medium: 'Mediu', low: 'Scăzut',
};
const SEV_ICON: Record<AlertSeverity, React.ReactNode> = {
  critical: <FireOutlined />,
  high:     <WarningOutlined />,
  medium:   <ExclamationCircleOutlined />,
  low:      <BellOutlined />,
};
const STAT_COLOR: Record<AlertStatus, string> = {
  active: '#ef4444', acknowledged: '#f59e0b', resolved: '#10b981',
};
const STAT_LABEL: Record<AlertStatus, string> = {
  active: 'Activă', acknowledged: 'Recunoscută', resolved: 'Rezolvată',
};

// ─── Export CSV ───────────────────────────────────────────

const exportAlertsCSV = (alerts: Alert[]) => {
  const header = 'ID,Senzor,Parametru,Valoare,Unitate,LOD,Severitate,Status,Locatie,Mesaj,Timestamp,RecunoscutDe,RezolvatLa';
  const rows   = alerts.map(a =>
    `${a.id},${a.sensorName},${a.parameter},${a.value},${a.unit},${a.lod},${a.severity},${a.status},"${a.location}","${a.message}",${a.timestamp},${a.acknowledgedBy ?? ''},${a.resolvedAt ?? ''}`
  );
  const csv    = [header, ...rows].join('\n');
  const blob   = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `alerte_simadca_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  message.success('Alerte exportate CSV!');
};

// ─── Export PDF ───────────────────────────────────────────

const exportAlertsPDF = (alerts: Alert[]) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W   = doc.internal.pageSize.getWidth();

  doc.setFillColor(99, 102, 241);
  doc.rect(0, 0, W, 18, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('SIMADCA v3.0 — Raport Alerte', 14, 12);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text(`Generat: ${new Date().toLocaleString('ro-RO')} · Total: ${alerts.length} alerte`, W - 14, 12, { align: 'right' });

  const cols = [45, 28, 22, 16, 16, 18, 22, 60];
  const heads = ['Senzor', 'Parametru', 'Valoare', 'LOD', 'Unitate', 'Severitate', 'Status', 'Mesaj'];
  let y = 28;

  doc.setFillColor(248, 250, 252);
  doc.rect(10, y - 5, W - 20, 7, 'F');
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  let xOff = 11;
  heads.forEach((h, i) => { doc.text(h, xOff, y); xOff += cols[i]; });
  y += 3;
  doc.setDrawColor(226, 232, 240); doc.line(10, y, W - 10, y); y += 3;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  alerts.slice(0, 25).forEach((a, idx) => {
    if (y > 185) { doc.addPage(); y = 16; }
    if (idx % 2 === 0) { doc.setFillColor(250, 250, 252); doc.rect(10, y - 3, W - 20, 6, 'F'); }
    const sevCol = a.severity === 'critical' ? [239, 68, 68] : a.severity === 'high' ? [249, 115, 22] : a.severity === 'medium' ? [245, 158, 11] : [132, 204, 22];
    const statCol = a.status === 'resolved' ? [16, 185, 129] : a.status === 'acknowledged' ? [245, 158, 11] : [239, 68, 68];
    const row = [a.sensorName, a.parameter, `${a.value} ${a.unit}`, String(a.lod), a.unit, SEV_LABEL[a.severity], STAT_LABEL[a.status], a.message];
    xOff = 11;
    row.forEach((val, ci) => {
      if (ci === 5) doc.setTextColor(...(sevCol as [number,number,number]));
      else if (ci === 6) doc.setTextColor(...(statCol as [number,number,number]));
      else doc.setTextColor(15, 23, 42);
      const t = val.length > 18 ? val.slice(0, 17) + '…' : val;
      doc.text(t, xOff, y); xOff += cols[ci];
    });
    y += 5;
  });

  doc.save(`alerte_simadca_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '_')}.pdf`);
  message.success('Raport PDF alerte generat!');
};

// ─── KPI mini ────────────────────────────────────────────

const KpiMini: React.FC<{ title: string; value: number; color: string; bg: string; icon: React.ReactNode; sub?: string }> =
  ({ title, value, color, bg, icon, sub }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color }}>{icon}</div>
      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{title}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 900, color, letterSpacing: '-0.5px' }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
  </div>
);

// ─── Alert card ───────────────────────────────────────────

const AlertCard: React.FC<{ alert: Alert; onAck: (id: string) => void; onResolve: (id: string) => void; onClick: () => void }> =
  ({ alert: a, onAck, onResolve, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'grid', gridTemplateColumns: '5px 1fr auto',
      gap: 14, padding: '14px 16px', cursor: 'pointer',
      borderBottom: '1px solid #f8fafc', alignItems: 'center',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
    onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
  >
    {/* Severity stripe */}
    <div style={{ width: 4, height: 40, borderRadius: 99, background: SEV_COLOR[a.severity], alignSelf: 'center' }} />

    {/* Content */}
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{a.sensorName}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: SEV_BG[a.severity], color: SEV_COLOR[a.severity], display: 'flex', alignItems: 'center', gap: 4 }}>
          {SEV_ICON[a.severity]} {SEV_LABEL[a.severity]}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: `${STAT_COLOR[a.status]}15`, color: STAT_COLOR[a.status] }}>
          {STAT_LABEL[a.status]}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 3 }}>
        <strong style={{ color: SEV_COLOR[a.severity] }}>{a.value} {a.unit}</strong> — {a.parameter}
        <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 11 }}>(LOD: {a.lod} {a.unit})</span>
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span><EnvironmentOutlined style={{ fontSize: 10 }} /> {a.location}</span>
        <span><ClockCircleOutlined style={{ fontSize: 10 }} /> {new Date(a.timestamp).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
        {a.acknowledgedBy && <span style={{ color: '#f59e0b' }}>ACK: {a.acknowledgedBy}</span>}
      </div>
    </div>

    {/* Actions */}
    {a.status !== 'resolved' && (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        {a.status === 'active' && (
          <button onClick={() => onAck(a.id)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, border: '1px solid #fde68a', background: '#fefce8', color: '#d97706', cursor: 'pointer', fontFamily: 'inherit' }}>
            ACK
          </button>
        )}
        <button onClick={() => onResolve(a.id)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontFamily: 'inherit' }}>
          Rezolvă
        </button>
      </div>
    )}
  </div>
);

// ─── Alert Modal ──────────────────────────────────────────

const AlertModal: React.FC<{ alert: Alert | null; onClose: () => void; onAck: (id: string) => void; onResolve: (id: string) => void }> =
  ({ alert, onClose, onAck, onResolve }) => {
  if (!alert) return null;
  const color = SEV_COLOR[alert.severity];

  return (
    <Modal open={!!alert} onCancel={onClose} footer={null} width={520} title={null} styles={{ body: { padding: 0 } }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', background: `${color}08`, borderBottom: '1px solid #f1f5f9', borderRadius: '18px 18px 0 0' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: SEV_BG[alert.severity], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color }}>{SEV_ICON[alert.severity]}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{alert.sensorName}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: SEV_BG[alert.severity], color }}>{SEV_LABEL[alert.severity]}</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: `${STAT_COLOR[alert.status]}15`, color: STAT_COLOR[alert.status] }}>{STAT_LABEL[alert.status]}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Valori */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {[
            { l: 'Valoare detectată', v: `${alert.value} ${alert.unit}`, c: color },
            { l: 'Limita de detecție', v: `${alert.lod} ${alert.unit}`, c: '#f59e0b' },
            { l: 'Maxim admis', v: `${alert.lod} ${alert.unit}`, c: '#64748b' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '10px', borderRadius: 9, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Mesaj */}
        <div style={{ fontSize: 13, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '10px 14px', lineHeight: 1.55 }}>
          {alert.message}
        </div>

        {/* Detalii */}
        {[
          ['Locație',   alert.location],
          ['Parametru', alert.parameter],
          ['Data/ora',  new Date(alert.timestamp).toLocaleString('ro-RO')],
          ['ID alertă', alert.id],
        ].map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', padding: '6px 0', borderBottom: i < 3 ? '1px solid #f8fafc' : 'none' }}>
            <span style={{ width: '40%', fontSize: 12, color: '#94a3b8' }}>{k}</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{v}</span>
          </div>
        ))}

        {/* Timeline */}
        <Timeline items={[
          { color: '#ef4444', children: <div><div style={{ fontWeight: 600, fontSize: 13 }}>Alertă generată</div><div style={{ fontSize: 11, color: '#64748b' }}>{new Date(alert.timestamp).toLocaleString('ro-RO')}</div></div> },
          ...(alert.acknowledgedBy ? [{ color: '#f59e0b', children: <div><div style={{ fontWeight: 600, fontSize: 13 }}>Recunoscută de {alert.acknowledgedBy}</div></div> }] : []),
          ...(alert.resolvedAt ? [{ color: '#10b981', children: <div><div style={{ fontWeight: 600, fontSize: 13 }}>Rezolvată</div><div style={{ fontSize: 11, color: '#64748b' }}>{new Date(alert.resolvedAt).toLocaleString('ro-RO')}</div></div> }] : [{ color: '#e2e8f0', children: <div style={{ fontSize: 12, color: '#94a3b8' }}>Așteptare rezolvare…</div> }]),
        ]} />

        {alert.status !== 'resolved' && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {alert.status === 'active' && (
              <Button icon={<CheckOutlined />} onClick={() => { onAck(alert.id); onClose(); }}>Recunoaște</Button>
            )}
            <Button type="primary" icon={<CheckCircleOutlined />} style={{ background: '#10b981', borderColor: '#10b981' }} onClick={() => { onResolve(alert.id); onClose(); }}>
              Marchează rezolvată
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const AlertPanel: React.FC = () => {
  const { user, logActivity } = useAuth();
  const { alerts: liveAlerts } = useSimulatorData();

  const [alerts,   setAlerts]   = useState<Alert[]>([]);
  const [tab,      setTab]      = useState<'active' | 'resolved' | 'all'>('active');
  const [search,   setSearch]   = useState('');
  const [sevF,     setSevF]     = useState('all');
  const [selected, setSelected] = useState<Alert | null>(null);
  const knownIds                = useRef<Set<string>>(new Set());

  // Sync cu simulatorul
  useEffect(() => {
    setAlerts(prev => {
      const prevMap = new Map(prev.map(a => [a.id, a]));
      const merged: Alert[] = [];
      liveAlerts.forEach(liveAlert => {
        merged.push(prevMap.has(liveAlert.id) ? prevMap.get(liveAlert.id)! : liveAlert);
      });
      prevMap.forEach((a, id) => {
        if (!liveAlerts.find(x => x.id === id)) merged.push(a);
      });
      return merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    });
  }, [liveAlerts]);

  const ack = (id: string) => {
    setAlerts(p => p.map(a => a.id === id ? { ...a, status: 'acknowledged', acknowledgedBy: user?.name ?? 'Operator' } : a));
    logActivity('Alertă recunoscută (ACK)', 'Alerte', id);
    message.success('Alertă recunoscută.');
  };

  const resolve = (id: string) => {
    setAlerts(p => p.map(a => a.id === id ? { ...a, status: 'resolved', resolvedAt: new Date().toISOString() } : a));
    logActivity('Alertă rezolvată', 'Alerte', id);
    message.success('Alertă rezolvată.');
  };

  const ackAll = () => {
    setAlerts(p => p.map(a => a.status === 'active' ? { ...a, status: 'acknowledged', acknowledgedBy: user?.name ?? 'Operator' } : a));
    logActivity('Toate alertele recunoscute', 'Alerte');
    message.success('Toate alertele active au fost recunoscute.');
  };

  const active   = alerts.filter(a => a.status === 'active').length;
  const critical = alerts.filter(a => a.severity === 'critical' && a.status === 'active').length;
  const ackd     = alerts.filter(a => a.status === 'acknowledged').length;
  const resolved = alerts.filter(a => a.status === 'resolved').length;

  const base = tab === 'active' ? alerts.filter(a => a.status !== 'resolved')
             : tab === 'resolved' ? alerts.filter(a => a.status === 'resolved')
             : alerts;

  const filtered = useMemo(() => base.filter(a => {
    const ms = search === '' ||
      a.sensorName.toLowerCase().includes(search.toLowerCase()) ||
      a.parameter.toLowerCase().includes(search.toLowerCase()) ||
      a.location.toLowerCase().includes(search.toLowerCase());
    return ms && (sevF === 'all' || a.severity === sevF);
  }), [base, search, sevF]);

  const sevData = (Object.keys(SEV_LABEL) as AlertSeverity[]).map(k => ({
    name: SEV_LABEL[k],
    total: alerts.filter(a => a.severity === k).length,
    active: alerts.filter(a => a.severity === k && a.status === 'active').length,
    color: SEV_COLOR[k],
  }));

  const TABS = [
    { key: 'active'   as const, label: 'Active & Recunoscute', count: active + ackd },
    { key: 'resolved' as const, label: 'Rezolvate',            count: resolved },
    { key: 'all'      as const, label: 'Toate',                count: alerts.length },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Alerte</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {active} active · {ackd} recunoscute · {resolved} rezolvate · sincronizat live
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {active > 0 && (
            <Popconfirm title="Recunoști toate alertele active?" onConfirm={ackAll} okText="Da" cancelText="Nu">
              <Button icon={<CheckOutlined />} danger>Recunoaște toate ({active})</Button>
            </Popconfirm>
          )}
          <Button icon={<FileExcelOutlined />} onClick={() => exportAlertsCSV(filtered)} style={{ borderColor: '#10b981', color: '#059669', background: '#f0fdf4' }}>
            Export CSV
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={() => exportAlertsPDF(filtered)} style={{ borderColor: '#6366f1', color: '#6366f1', background: '#eef2ff' }}>
            Export PDF
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <KpiMini title="Alerte active"   value={active}   color="#ef4444" bg="#fee2e2" icon={<AlertOutlined />}       sub="Necesită atenție" />
        <KpiMini title="Critice active"  value={critical} color="#7f1d1d" bg="#fecaca" icon={<FireOutlined />}        sub="Intervenție imediată" />
        <KpiMini title="Recunoscute"     value={ackd}     color="#d97706" bg="#fef3c7" icon={<ClockCircleOutlined />} sub="În monitorizare" />
        <KpiMini title="Rezolvate total" value={resolved} color="#059669" bg="#d1fae5" icon={<CheckCircleOutlined />} sub="Istoric" />
      </div>

      {/* Bar chart severitate */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Distribuție pe severitate</div>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={sevData} margin={{ top: 4, right: 8, bottom: 0, left: -24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11, border: '1px solid #e2e8f0' }} formatter={(v: any, n: any) => [v, n === 'total' ? 'Total' : 'Active']} />
            <Bar dataKey="total"  name="total"  fill="#f1f5f9" radius={[4,4,0,0]} />
            <Bar dataKey="active" name="active" radius={[4,4,0,0]}>{sevData.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Lista alerte */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {/* Tabs + filtre */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '0 16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '14px 16px', fontSize: 13,
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? '#6366f1' : '#64748b',
              border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${tab === t.key ? '#6366f1' : 'transparent'}`,
              marginBottom: -1, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 7,
            }}>
              {t.label}
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: tab === t.key ? '#eef2ff' : '#f8fafc', color: tab === t.key ? '#6366f1' : '#94a3b8' }}>
                {t.count}
              </span>
            </button>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0' }}>
            <Input
              placeholder="Caută senzor, parametru…"
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={search} onChange={e => setSearch(e.target.value)}
              allowClear size="small" style={{ width: 200 }}
            />
            <Select value={sevF} onChange={setSevF} size="small" style={{ width: 140 }}>
              <Option value="all">Toate severitățile</Option>
              {(Object.entries(SEV_LABEL) as [AlertSeverity, string][]).map(([k, v]) => (
                <Option key={k} value={k}><span style={{ color: SEV_COLOR[k], fontWeight: 600 }}>{v}</span></Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Rows */}
        <div>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
              <CheckCircleOutlined style={{ fontSize: 32, color: '#10b981', display: 'block', marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {tab === 'active' ? 'Nicio alertă activă' : 'Nicio alertă găsită'}
              </div>
            </div>
          ) : (
            filtered.map(a => (
              <AlertCard key={a.id} alert={a} onAck={ack} onResolve={resolve} onClick={() => setSelected(a)} />
            ))
          )}
        </div>
      </div>

      <AlertModal alert={selected} onClose={() => setSelected(null)} onAck={ack} onResolve={resolve} />
    </div>
  );
};

export default AlertPanel;